import { toDate } from "@flaminem/utils";

import {
  AlertFragmentFragment,
  AlertSummaryFragmentFragment,
  AlertStatus as ApiAlertStatus,
  AlertOrigin as ApiAlertOrigin,
  AlertValidationDecision as ApiAlertValidationDecision,
  ShortAlertFragmentFragment,
} from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { mapToAdminStatus } from "src/mappers/adminStatus.mappers";
import { mapToCase, mapToCaseSummary, mapToShortCaseElement } from "src/mappers/case.mappers";
import { mapToScenarioExecutionResultWithCase } from "src/mappers/scenario.mappers";
import { mapToUser } from "src/mappers/user.mappers";
import { getPropertyValueByCode } from "src/services/property/getPropertyByCode";
import { getFullName } from "src/services/user/getUserFullName";
import {
  Alert,
  AlertOrigin,
  AlertStatus,
  AlertSummary,
  AlertValidationDecision,
  ShortAlert,
} from "src/types/alert";
import { hasType } from "src/types/typedValue";

export const mapToShortAlert = (alert: ShortAlertFragmentFragment): ShortAlert => {
  const assignee = alert.assignee ? mapToUser(alert.assignee) : null;

  return {
    id: alert.id,
    status: mapToAlertStatus(alert.status),
    decision: alert.decision ? mapToAlertValidationDecision(alert.decision) : null,
    assignee,
    adminStatus: mapToAdminStatus(alert.adminStatus),
    isAssigned: assignee !== null,
    assigneeFullName: assignee ? getFullName(assignee) : "",
    typeCode: alert.caseVersions?.current?.typeCode || "",
    origin: mapToAlertOrigin(alert.origin),
  };
};

export const mapToAlert = (alert: AlertFragmentFragment): Alert => {
  const currentCase = alert.caseVersions.current ? mapToCase(alert.caseVersions.current) : null;
  const creationCase = alert.caseVersions.creation ? mapToCase(alert.caseVersions.creation) : null;

  const validationCase = alert.caseVersions.validation
    ? mapToCase(alert.caseVersions.validation)
    : null;

  if (!currentCase) {
    throw new Error("Current case is not expected to be null in alert");
  }

  const alertStatus = mapToAlertStatus(alert.status);
  const isAlertValidated = alertStatus === "closed";
  const isAlertCanceled = alertStatus === "canceled";
  const dateProperty = getPropertyValueByCode(currentCase.properties ?? [], "date");
  const idProperty = getPropertyValueByCode(currentCase.properties ?? [], "id");

  return {
    ...mapToShortAlert(alert),
    comment: alert.comment,
    id: alert.id,
    currentCase,
    creationCase,
    validationCase,
    isAlertValidated,
    isAlertCanceled,
    requestedDate:
      dateProperty && hasType(dateProperty.value, "date") ? dateProperty.value.value : null,
    caseImportedDate:
      currentCase && currentCase.adminStatus ? toDate(currentCase.adminStatus.creation) : null,
    validatorFullName: isAlertValidated ? getFullName(alert.adminStatus.lastModifier) : null,
    validationDate: isAlertValidated ? toDate(alert.adminStatus.lastModification) : null,
    cancelledDate: isAlertCanceled ? toDate(alert.adminStatus.lastModification) : null,
    globalScore: alert.scenarioExecutionResult?.globalScore ?? 0,
    scenarioName: alert.scenarioExecutionResult?.scenarioVersion?.scenario?.name ?? "",
    assigneeFullName: alert.assignee ? getFullName(alert.assignee) : "",
    alertNumber: idProperty && hasType(idProperty.value, "string") ? idProperty.value.value : "",
    creationDate: toDate(alert.adminStatus.creation),
    scenarioExecutionResult: alert.scenarioExecutionResult
      ? mapToScenarioExecutionResultWithCase(alert.scenarioExecutionResult)
      : null,
    caseScoringDate: alert.scenarioExecutionResult?.date
      ? toDate(alert.scenarioExecutionResult?.date)
      : null,
    fraudType: alert.fraudType?.name ?? null,
    status: mapToAlertStatus(alert.status),
    attachments: alert.attachments.map((attachment) => ({
      id: attachment.id,
      name: attachment.name,
      fileId: attachment.fileId,
      contentType: attachment.contentType,
      currentVersionId: attachment.currentVersionId,
    })),
    decision: alert.decision ? mapToAlertValidationDecision(alert.decision) : null,
    financialLossAmount: alert.financialLossAmount,
    preventedFraudAmount: alert.preventedFraudAmount,
  };
};

export const mapToAlertSummary = (alert: AlertSummaryFragmentFragment): AlertSummary => {
  const currentCase = alert.caseVersions.current
    ? {
        ...mapToCaseSummary(alert.caseVersions.current),
      }
    : null;

  if (!currentCase) {
    throw new Error("Case is not expected to be null");
  }

  return {
    ...mapToShortAlert(alert),
    nbrOfIdentifiedCases: alert.nbrOfIdentifiedCases,
    currentCase,
    creationCase: alert.caseVersions.creation
      ? mapToCaseSummary(alert.caseVersions.creation)
      : null,
    validationCase: alert.caseVersions.validation
      ? mapToCaseSummary(alert.caseVersions.validation)
      : null,
    scenarioExecutionResult: alert.scenarioExecutionResult
      ? {
          globalScore: alert.scenarioExecutionResult.globalScore,
          case: alert.scenarioExecutionResult.case
            ? {
                id: alert.scenarioExecutionResult.case.id,
                elements: alert.scenarioExecutionResult.case.elements.map(mapToShortCaseElement),
              }
            : null,
          scenarioVersion: alert.scenarioExecutionResult.scenarioVersion
            ? {
                name: alert.scenarioExecutionResult.scenarioVersion.name,
                scenario: alert.scenarioExecutionResult.scenarioVersion.scenario
                  ? {
                      id: alert.scenarioExecutionResult.scenarioVersion.scenario.id,
                      name: alert.scenarioExecutionResult.scenarioVersion.scenario.name,
                    }
                  : null,
              }
            : null,
        }
      : null,
  };
};

const ALERT_ORIGIN_MAP: Record<ApiAlertOrigin, AlertOrigin> = {
  MANUAL: "manual",
  SCORE: "score",
};

export const mapToAlertOrigin = (origin: ApiAlertOrigin): AlertOrigin => {
  return ALERT_ORIGIN_MAP[origin];
};

const ALERT_STATUS_MAP: Record<ApiAlertStatus, AlertStatus> = {
  CLOSED: "closed",
  NOT_ASSIGNED: "notAssigned",
  ASSIGNED: "assigned",
  CANCELED: "canceled",
};

export const mapToAlertStatus = (status: ApiAlertStatus): AlertStatus => {
  return ALERT_STATUS_MAP[status];
};

const ALERT_STATUS_MAP_REVERSE = Object.entries(ALERT_STATUS_MAP).reduce(
  (reverseMap, [apiStatus, status]) => {
    reverseMap[status] = apiStatus as ApiAlertStatus;
    return reverseMap;
  },
  {} as Record<AlertStatus, ApiAlertStatus>
);

export const mapToApiAlertStatus = (status: AlertStatus): ApiAlertStatus => {
  return ALERT_STATUS_MAP_REVERSE[status];
};

const ALERT_VALIDATION_DECISION_MAP: Record<ApiAlertValidationDecision, AlertValidationDecision> = {
  COMPLIANT: "compliant",
  FRAUD_PREVENTED: "fraudPrevented",
  PROVEN_FRAUD_WITH_FINANCIAL_LOSS: "provenFraudWithFinancialLoss",
  PROVEN_FRAUD_WITH_NO_FINANCIAL_LOSS: "provenFraudWithNoFinancialLoss",
};

export const mapToAlertValidationDecision = (
  decision: ApiAlertValidationDecision
): AlertValidationDecision => {
  return ALERT_VALIDATION_DECISION_MAP[decision];
};

export const mapToAlertValidationDecisionFromCode = (
  decisionCode: string
): AlertValidationDecision | undefined => {
  if (decisionCode in ALERT_VALIDATION_DECISION_MAP) {
    return ALERT_VALIDATION_DECISION_MAP[decisionCode as ApiAlertValidationDecision];
  }
  return undefined;
};

// Generate reverse mapping dynamically
const ALERT_VALIDATION_DECISION_MAP_REVERSE = Object.entries(ALERT_VALIDATION_DECISION_MAP).reduce(
  (reverseMap, [apiDecision, decision]) => {
    reverseMap[decision] = apiDecision as ApiAlertValidationDecision;
    return reverseMap;
  },
  {} as Record<AlertValidationDecision, ApiAlertValidationDecision>
);

export const mapToApiAlertValidationDecision = (
  decision: AlertValidationDecision
): ApiAlertValidationDecision => {
  return ALERT_VALIDATION_DECISION_MAP_REVERSE[decision];
};
